import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../api.js';
import PageHeader from '../../components/PageHeader.jsx';
import { Icon, SECTION_COLOR } from '../../icons.jsx';
import TrailerFormModal from './TrailerFormModal.jsx';

const TrashIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/></svg>;

const TOOLBAR_ACTIONS = [
  { label: 'Print', icon: Icon.filePlus, disabled: true },
  { label: 'Download', icon: Icon.upload, disabled: true },
  { label: 'Active Columns', icon: Icon.columns, disabled: true }
];

export default function TrailerList() {
  const [trailers, setTrailers] = useState(null);
  const [query, setQuery] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  function load() {
    setTrailers(null);
    api('/dispatch/trailers').then(setTrailers).catch(() => setTrailers([]));
  }

  const deleteTrailer = async (e, t) => {
    e.stopPropagation();
    if (!window.confirm(`Delete unit "${t.unit_number}"? This can't be undone.`)) return;
    try {
      await api(`/dispatch/trailers/${t.id}`, { method: 'DELETE' });
      load();
    } catch (err) {
      alert(err.message);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!trailers) return [];
    const q = query.trim().toLowerCase();
    if (!q) return trailers;
    return trailers.filter(t =>
      [t.unit_number, t.ref_number, t.trailer_type, t.plate, t.vin]
        .filter(Boolean).some(v => v.toLowerCase().includes(q)));
  }, [trailers, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageRows = filtered.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div>
      <PageHeader
        icon={Icon.control}
        title="Trailers"
        subtitle="Fleet trailers — profile, documents, repairs, and related loads"
        color={SECTION_COLOR.control}
        actions={
          <>
            <button className="primary-btn" onClick={() => { setEditing(null); setFormOpen(true); }}>+ New trailer</button>
            {TOOLBAR_ACTIONS.map(a => (
              <button key={a.label} className="toolbar-btn" disabled={a.disabled} title={`${a.label} — coming soon`}>
                <a.icon />
                <span>{a.label}</span>
                <span className="nav-flyout-soon">Soon</span>
              </button>
            ))}
            <button className="topbar-icon-btn" title="Search"><Icon.search /></button>
          </>
        }
      />

      <div className="panel filter-row-panel">
        <div className="filter-row" style={{ justifyContent: 'space-between', width: '100%' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input placeholder="Type to filter..." value={query}
              onChange={e => { setQuery(e.target.value); setPage(1); }}
              style={{ width: 240 }} />
            <button className="topbar-icon-btn" title="Search"><Icon.search /></button>
          </div>
          <label style={{ fontSize: 13, color: 'var(--text-dim)', display: 'flex', alignItems: 'center', gap: 6 }}>
            Show:
            <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }} style={{ width: 70 }}>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </label>
        </div>
      </div>

      {trailers === null && <div className="empty">Loading...</div>}
      {trailers && trailers.length === 0 && <div className="empty">No trailers yet — click "+ New trailer" above.</div>}

      {trailers && trailers.length > 0 && (
        <>
          <table>
            <thead>
              <tr>
                <th>Ref#</th>
                <th>Unit No</th>
                <th>Type</th>
                <th>Plate</th>
                <th>VIN</th>
                <th>Year</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map(t => (
                <tr key={t.id} onClick={() => { setEditing(t); setFormOpen(true); }} style={{ cursor: 'pointer' }}>
                  <td>{t.ref_number || '—'}</td>
                  <td>{t.unit_number}</td>
                  <td>{t.trailer_type || '—'}</td>
                  <td>{t.plate || '—'}</td>
                  <td>{t.vin || '—'}</td>
                  <td>{t.year || '—'}</td>
                  <td>{t.dispatch_status || 'Active'}</td>
                  <td>
                    <button type="button" title="Delete trailer" onClick={e => deleteTrailer(e, t)}
                      style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 30, height: 30, border: '1px solid var(--border)', background: 'var(--surface)',
                        color: 'var(--red)', cursor: 'pointer', padding: 0
                      }}>
                      <TrashIcon />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, fontSize: 13, color: 'var(--text-dim)' }}>
            <span>Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, filtered.length)} of {filtered.length} entries</span>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <button className="page-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>←</button>
              <span className="page-btn" style={{ background: 'var(--blue)', color: '#fff', cursor: 'default' }}>{page}</span>
              <button className="page-btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>→</button>
            </div>
          </div>
        </>
      )}

      {formOpen && (
        <TrailerFormModal
          trailer={editing}
          onClose={() => setFormOpen(false)}
          onSaved={() => { setFormOpen(false); load(); }}
        />
      )}
    </div>
  );
}
